/**
 * LightPoise — pure logic, no Phaser.
 *
 * Tracks consecutive light hits landing on an enemy. After `threshold`
 * consecutive light hits the enemy shrugs off the hitstun on that hit and
 * the counter resets (poise break → normal recovery → poise returns).
 *
 * Heavy hits (damage >= HEAVY_HIT_THRESHOLD), throws, and launchers always
 * break through poise and reset the counter; they are NOT suppressed.
 *
 * Separation contract: this module MUST NOT import Phaser.
 */

import { HEAVY_HIT_THRESHOLD } from './JuggernautArmour';

/** Frames after the last light hit before the streak resets (1.5 s at 60 fps). */
export const POISE_WINDOW_FRAMES = 90;

/** Default poise thresholds by enemy tier. */
export const POISE_THRESHOLD_BY_TIER: Record<string, number> = {
  galsia: 3,
  donovan: 4,
  signal: 4,
  elite: 6,
  boss: 8,
};

export interface LightPoiseState {
  /** Consecutive light hits since last reset. */
  lightHitCount: number;
  /** Frames remaining in the current poise window. Resets on each light hit. */
  windowFrames: number;
}

export function initialPoiseState(): LightPoiseState {
  return { lightHitCount: 0, windowFrames: 0 };
}

/**
 * Call each game-frame. Expires the window and resets count when it runs out.
 */
export function tickLightPoise(state: LightPoiseState): void {
  if (state.windowFrames > 0) {
    state.windowFrames -= 1;
    if (state.windowFrames === 0) {
      state.lightHitCount = 0;
    }
  }
}

/**
 * Call when the entity receives a hit.
 *
 * Returns `true` when the hitstun for this hit should be suppressed (shrug-off).
 * Mutates `state`.
 *
 * @param state      Mutable poise state for this entity.
 * @param damage     Incoming hit damage.
 * @param threshold  Light-hit count before a shrug-off occurs.
 * @param isThrow    True when the hit originates from a throw — always heavy.
 * @param isLauncher True when the hit has the launcher flag — always heavy.
 */
export function applyHitToPoise(
  state: LightPoiseState,
  damage: number,
  threshold: number,
  isThrow = false,
  isLauncher = false,
): boolean {
  const isHeavy = isThrow || isLauncher || damage >= HEAVY_HIT_THRESHOLD;

  if (isHeavy) {
    state.lightHitCount = 0;
    state.windowFrames = 0;
    return false;
  }

  state.lightHitCount += 1;
  state.windowFrames = POISE_WINDOW_FRAMES;

  if (state.lightHitCount >= threshold) {
    state.lightHitCount = 0;
    state.windowFrames = 0;
    return true; // suppress hitstun — shrug-off
  }

  return false;
}
